import { VSF_CURRENCY_COOKIE } from '~~/constants/core-constants';
import { i18nToLanguageAndCountry } from '~~/helpers/locales/i18nCurrentDetails';
import { pushCleanTags } from '~/plugins/integrations/tagManager/helpers/pushCleanTags/pushCleanTags';
import {
  buildBundleBuilderAddToCartTags,
  buildBundleBuilderClickItemTags,
  buildBundleBuilderViewTags,
  buildClearAllFiltersParameters,
  buildClearSingleFiltersParameter,
  buildClickAccessoryTags,
  buildClickBreadcrumbsTags,
  buildClickComplementaryTags,
  buildClickFilterTags,
  buildClickHitTags,
  buildClickParentProductTags,
  buildClickPromoBannerTags,
  buildConversionTags,
  buildFormStartTags,
  buildFormSubmitTags,
  buildPageViewTags,
  buildQuantityChangeTags,
  buildRecommendationsShownTags,
  buildSearchResultsTags,
  buildViewComplementaryTags,
  buildViewHitsTags,
} from '~/plugins/integrations/tagManager/helpers';
import {
  buildAddToCartEvent,
  buildBeginCheckoutEvent,
  buildClickAlternativeProductEvent,
  buildClickProductBoxEvent,
  buildPayNowEvent,
  buildProductReviewEvent,
  buildPromotionEvent,
  buildPurchaseEvent,
  buildRemarketingEvent,
  buildRemoveFromCartEvent,
  buildViewAccessoriesEvent,
  buildViewCartRecommendationsEvent,
  buildViewItemListEvent,
  buildViewParentProductsEvent,
  buildViewProductEvent,
  buildViewSimilarProductsEvent,
} from '~/plugins/integrations/tagManager/helpers/trackingHelpers';
import type {
  BundleBuilderBaseParameter,
  RemoveFromCartTagDetailsParams,
  TriggerAddToCartTagsParameters,
  TriggerClearAllFiltersParameters,
  TriggerClearSingleFiltersParameterWrapper,
  TriggerClickBreadcrumbsParameters,
  TriggerClickFilterParameters,
  TriggerClickHitEventParameters,
  TriggerClickProducBoxParameters,
  TriggerClickPromoBannerParameters,
  TriggerConversionEventParameters,
  TriggerFormStartParameters,
  TriggerFormSubmitParameters,
  TriggerPayNowParameters,
  TriggerPageViewParameters,
  TriggerProductReviewTagsParameters,
  TriggerPromotionTagsParameters,
  TriggerQuantityChangeParameters,
  TriggerViewHitsEventParameters,
  TriggerViewItemListParameters,
} from '~~/types/integrations/tagManager/TagManagerIntegration';
import type { Cart, Order, ProductVariant } from '~~/types/commercetools';
import type { ProductVariantExtended } from '~~/types/product/ProductVariantExtended';
import type { RecommendationTypeName } from '~~/types/localization/localizationHelperTypes';
import type { TagProductBox } from '~~/types/integrations/tagManager/TagProductBox';
import type { RemarketingPageType } from '~~/types/integrations/tagManager/RemarketingPageType';
import type { PurchaseUserInfo } from '~~/types/integrations/tagManager/PurchaseTypes';
import { useGtmClient } from '~/composables/useGtmClient';

const useLanguageAndCurrency = () => {

  const { $i18n } = useNuxtApp();
  const currencyCookie = useCookie(VSF_CURRENCY_COOKIE);

  return {
    languageAndCountry: i18nToLanguageAndCountry($i18n),
    currency: currencyCookie.value || '',
  };

};

export const useGtmEvents = () => {

  const gtm = useGtmClient();
  const { languageAndCountry, currency } = useLanguageAndCurrency();

  const push = (payload: Record<string, unknown>) => {

    gtm.push(payload);

  };

  const pushEcommerce = (payload: Record<string, unknown>) => {

    pushCleanTags(gtm, payload);

  };

  return {
    trackPageView: (params: TriggerPageViewParameters) =>
      push(buildPageViewTags({ page: params.page, user: params.user })),
    trackViewItemList: (params: TriggerViewItemListParameters) =>
      pushEcommerce(buildViewItemListEvent(params, languageAndCountry)),
    trackSearchResults: (searchTerm: string) => push(buildSearchResultsTags(searchTerm)),
    trackClickBreadcrumbs: (params: TriggerClickBreadcrumbsParameters) =>
      push(buildClickBreadcrumbsTags(params)),

    trackPromotion: (params: TriggerPromotionTagsParameters) =>
      pushEcommerce(buildPromotionEvent(params)),
    trackPurchase: ({
      cartOrOrder,
      products,
      orderNumber,
      deliveryDate,
      paymentType,
      paymentName,
      purchaseUserInfo,
    }: {
      cartOrOrder: Cart | Order;
      products: ProductVariant[];
      orderNumber: Order['orderNumber'];
      deliveryDate?: string;
      paymentType?: string;
      paymentName?: string;
      purchaseUserInfo?: PurchaseUserInfo;
    }) => pushEcommerce(
      buildPurchaseEvent({
        cartOrOrder,
        products,
        orderNumber,
        languageAndCountry,
        currency,
        deliveryDate,
        paymentType,
        paymentName,
        purchaseUserInfo,
      }),
    ),
    trackPayNow: (params: TriggerPayNowParameters) =>
      pushEcommerce(buildPayNowEvent({ params, languageAndCountry, currency })),
    trackViewProduct: (params: { product: ProductVariant; inStock: boolean; eventName?: string }) =>
      pushEcommerce(buildViewProductEvent({ params, languageAndCountry, currency })),
    trackAddToCart: (params: TriggerAddToCartTagsParameters) =>
      pushEcommerce(buildAddToCartEvent({ params, languageAndCountry, currency })),
    trackRemoveFromCart: (product: ProductVariant, details: RemoveFromCartTagDetailsParams) =>
      pushEcommerce(buildRemoveFromCartEvent({ product, details, languageAndCountry, currency })),
    trackQuantityChange: (params: TriggerQuantityChangeParameters) =>
      push(buildQuantityChangeTags(params)),
    trackBeginCheckout: (cart: Cart, products: ProductVariant[]) =>
      pushEcommerce(buildBeginCheckoutEvent({ cart, products, languageAndCountry, currency })),

    trackClickAccessory: (productSku: string, parentProductSku: string) =>
      pushEcommerce(buildClickAccessoryTags(productSku, parentProductSku)),
    trackViewAccessories: (products: ProductVariant[], parentProductSku: string) =>
      pushEcommerce(buildViewAccessoriesEvent(products, parentProductSku)),
    trackClickParentProduct: (parentSku: string, accessorySku: string) =>
      pushEcommerce(buildClickParentProductTags(parentSku, accessorySku)),
    trackViewParentProducts: (products: ProductVariant[], accessoryProductSku: string) =>
      pushEcommerce(buildViewParentProductsEvent(products, accessoryProductSku)),

    trackViewComplementary: (skus: string[]) =>
      pushEcommerce(buildViewComplementaryTags(skus)),
    trackClickComplementary: (sku: string) =>
      pushEcommerce(buildClickComplementaryTags(sku)),
    trackViewSimilarProducts: (products: ProductVariant[], sourceProductSku: string) =>
      pushEcommerce(buildViewSimilarProductsEvent(products, sourceProductSku)),
    trackViewCartRecommendations: (products: ProductVariantExtended[], sourceProductSku: string) =>
      pushEcommerce(buildViewCartRecommendationsEvent(products, sourceProductSku)),

    trackRecommendationsShown: (productsTags: TagProductBox[], recommendationType?: RecommendationTypeName) =>
      pushEcommerce(buildRecommendationsShownTags({
        recommendationType,
        productTags: productsTags,
      })),

    trackClickProductBox: (params: TriggerClickProducBoxParameters) =>
      pushEcommerce(buildClickProductBoxEvent(params)),
    trackClickAlternativeProduct: (isAware: boolean, productSku: string) =>
      pushEcommerce(buildClickAlternativeProductEvent(isAware, productSku)),

    trackClickFilter: (params: TriggerClickFilterParameters) =>
      push(buildClickFilterTags(params)),
    trackClearSingleFilter: (params: TriggerClearSingleFiltersParameterWrapper) =>
      push(buildClearSingleFiltersParameter(params)),
    trackClearAllFilters: (params: TriggerClearAllFiltersParameters) =>
      push(buildClearAllFiltersParameters(params)),
    trackClickPromoBanner: (params: TriggerClickPromoBannerParameters) =>
      push(buildClickPromoBannerTags(params)),
    trackFormStart: (params: TriggerFormStartParameters) =>
      push(buildFormStartTags(params)),
    trackFormSubmit: (params: TriggerFormSubmitParameters) =>
      push(buildFormSubmitTags(params)),

    trackAddToCartBundleBuilder: (params: BundleBuilderBaseParameter) =>
      pushEcommerce(buildBundleBuilderAddToCartTags(params)),
    trackClickItemBundleBuilder: (params: BundleBuilderBaseParameter) =>
      pushEcommerce(buildBundleBuilderClickItemTags(params)),
    trackViewBundleBuilder: (params: BundleBuilderBaseParameter) =>
      pushEcommerce(buildBundleBuilderViewTags(params)),

    trackViewHitsEvent: (params: TriggerViewHitsEventParameters) =>
      push(buildViewHitsTags(params)),
    trackClickHitEvent: (params: TriggerClickHitEventParameters) =>
      push(buildClickHitTags(params)),
    trackConversionEvent: (params: TriggerConversionEventParameters) =>
      push(buildConversionTags(params)),

    trackRemarketing: (purpose: RemarketingPageType, product: ProductVariant) =>
      push(buildRemarketingEvent(purpose, product)),

    trackProductReview: (params: TriggerProductReviewTagsParameters) =>
      push(buildProductReviewEvent(params)),
  };

};
